/**
 * Shared layout constants for wire `points_bin` and runtime {@link CompactShape}.
 *
 * Wire record ({@link SHAPE_POINTS_BIN_RECORD_BYTES} bytes, LE) and runtime stride
 * ({@link COMPACT_SHAPE_STRIDE} Float64 slots) describe the same logical point;
 * `fromBuffer` promotes f32 wire fields to f64 in the compact buffer.
 *
 * @see ShapeWire in `src/types/dtos/realtime-api/realtime.dto.ts`
 * @see Wiki/Realtime Transports/1-Realtime-transports.md §8.2
 */

/**
 * Wire record size for one shape point in {@link ShapeWire.points_bin} (little-endian).
 *
 * Layout: f32 lat, f32 lon, f32 cumDist, u16 bearing×10.
 */
export const SHAPE_POINTS_BIN_RECORD_BYTES = 14;

/**
 * Runtime {@link CompactShape} stride: one Float64 slot per logical field per point.
 *
 * Interleaved layout: `[lat, lon, cumDist, bearingX10]` repeated per point index.
 */
export const COMPACT_SHAPE_STRIDE = 4;

/** Index of latitude (degrees) within a compact shape point stride. */
export const COMPACT_SHAPE_LAT = 0;

/** Index of longitude (degrees) within a compact shape point stride. */
export const COMPACT_SHAPE_LON = 1;

/** Index of cumulative distance (meters from shape start) within a compact shape point stride. */
export const COMPACT_SHAPE_CUM_DIST = 2;

/**
 * Index of bearing×10 (wire u16, not degrees) within a compact shape point stride.
 *
 * Divide by 10 when building {@link CompactTimeline} headings.
 */
export const COMPACT_SHAPE_BEARING_X10 = 3;

/** Approximate bytes per compact shape point (stride × Float64). */
export const COMPACT_SHAPE_BYTES_PER_POINT = COMPACT_SHAPE_STRIDE * 8;

/**
 * Approximate bytes per compact timeline point (5×Float64 + Uint16 + Uint8 columns).
 * Used by memory estimators and diagnostics docs only.
 */
export const COMPACT_TIMELINE_BYTES_PER_POINT = 8 * 4 + 2 + 1;
